import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStartups, useInvalidateStartups, projectService, type StartupDto } from '@ideoz/core';
import {
  Button,
  ConfirmProjectModal,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Card,
  CardContent,
  cn,
  AppLayout,
  Header,
} from '@ideoz/ui';
import { Ellipsis, Folder, FolderMinus, Pencil, Plus, Rocket, Trash2 } from 'lucide-react';
import { useMyProfile } from '../modules/profile/useMyProfile';

export default function StartupListPage(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: startups = [], isLoading: isFetching } = useStartups();
  const invalidateStartups = useInvalidateStartups();

  const [startupToDelete, setStartupToDelete] = useState<StartupDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Wear the shared app shell + account sidebar (My Startups / Profile / Billing
  // / App Settings), with "My Startups" active — same as /profile, /billing,
  // /app-settings. `useMyProfile` is reused here purely for that shell.
  const { user, facade } = useMyProfile('startups');
  const {
    header: {
      headerProps,
      userMenuItems,
      userMenuSocialLinks,
      onRequestUpgrade,
      isRequestingUpgrade,
    },
    subscriptionExpiration: { isExpiringWithin3Days, daysUntilExpiration },
    onRenewSubscription,
  } = facade;

  const handleConfirmDelete = async () => {
    if (!startupToDelete) return;
    setIsDeleting(true);
    try {
      await projectService.deleteStartup(startupToDelete.id);
      await invalidateStartups();
      setStartupToDelete(null);
    } catch (err) {
      console.error('Failed to delete startup:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AppLayout
      currentPath="/startups"
      sidebar={facade.sidebar}
      user={{ isLoggedin: !!user, name: user?.name, avatar: user?.avatar }}
      userMenuProps={
        user
          ? {
              points: headerProps.points,
              pointsLabel: t('profile.pointsLabel'),
              menuItems: userMenuItems || [],
              socialLinks: userMenuSocialLinks || [],
              onRequestUpgrade,
              isRequestingUpgrade: isRequestingUpgrade || false,
              canRequestUpgrade: (headerProps.points || 0) <= 0,
              isWeb: true,
            }
          : undefined
      }
      subscriptionExpirationBanner={
        isExpiringWithin3Days
          ? { isVisible: true, daysUntilExpiration, onRenew: onRenewSubscription }
          : undefined
      }
      isWeb
      testId="startups-sidemenu"
      height="screen"
      defaultSidebarCollapsed={false}
    >
      <Header
        {...headerProps}
        showBackButton
        showBackArrow={false}
        backText={t('startups.title')}
        isHasBorder
        className="sticky top-0 w-full"
        isWeb
      />

      <section className="mx-auto w-full max-w-6xl px-6 py-10">
        <header className="mb-6 flex flex-col gap-2">
          <h1 className="text-2xl text-start font-semibold text-gray-800">{t('startups.title')}</h1>
          <p className="text-gray-600 text-sm">{t('startups.subtitle')}</p>
        </header>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {
            <Card
              onClick={() => navigate('/onboarding')}
              className={cn(
                'group cursor-pointer border border-dashed min-h-48 rounded-3xl',
                'hover:border-primary hover:bg-blue-light-100/20 transition-all duration-200'
              )}
            >
              <CardContent className="p-6 flex flex-col items-center justify-center h-full min-h-[180px]">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Plus className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-medium lg:font-semibold text-lg text-card-foreground">
                  {t('startups.addNew')}
                </h3>
                <p className="text-sm text-muted-foreground text-center mt-2">
                  {t('startups.configure')}
                </p>
              </CardContent>
            </Card>
          }
          {isFetching ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            startups.map((startup: StartupDto) => (
              <Card
                key={startup.id}
                onClick={() => navigate(`/${startup.slug}/work-scopes`)}
                className="group hover:shadow-sm transition-all duration-200 cursor-pointer border h-full min-h-48 rounded-3xl"
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                      <Rocket className="w-6 h-6 text-primary" />
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          aria-label={t('startups.actionsAria', { name: startup.name })}
                          data-testid={`startup-actions-${startup.id}`}
                        >
                          <Ellipsis size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="border p-0 shadow-md">
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/${startup.slug}/profile`);
                          }}
                          className="gap-2 cursor-pointer"
                        >
                          <Pencil className="h-3.5 w-3.5" /> {t('startups.edit')}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setStartupToDelete(startup);
                          }}
                          className="gap-2 cursor-pointer text-red-600 focus:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" /> {t('startups.delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h2 className="font-medium lg:font-semibold text-lg mb-2 text-card-foreground line-clamp-1">
                    {startup.name}
                  </h2>
                  {(() => {
                    // Description may be rich-text HTML — strip tags for the
                    // card preview and skip rendering when there's nothing left.
                    const preview = (startup.description ?? '')
                      .replace(/<[^>]+>/g, '')
                      .replace(/&nbsp;/g, ' ')
                      .trim();
                    if (!preview) return null;
                    return <p className="text-sm text-muted-foreground line-clamp-2">{preview}</p>;
                  })()}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Delete Startup — type-to-confirm, same pattern as projects/scopes */}
        <ConfirmProjectModal
          isOpen={!!startupToDelete}
          onClose={() => setStartupToDelete(null)}
          onCreate={handleConfirmDelete}
          title={t('startups.deleteDialog.title')}
          info={{
            title: t('startups.deleteDialog.infoTitle'),
            desc: t('startups.deleteDialog.infoDesc'),
          }}
          buttonText={t('startups.deleteDialog.button')}
          buttonType="danger"
          iconName={FolderMinus}
          inputLabel={t('startups.deleteDialog.inputLabel')}
          expectedProjectName={startupToDelete?.name}
          targetAction="delete"
          isLoading={isDeleting}
        />
      </section>
    </AppLayout>
  );
}
